import { useEffect, useState, type FormEvent } from "react";
import { useProcedure, useQuery } from "highscale/react";
import { at } from "./lexicons";
import { RoundWorkspace } from "./RoundWorkspace";
import { RoundPlanFields, useRoundPlan } from "./RoundPlanFields";
export function useLeagueRoute() {
  const [route, setRoute] = useState(() => window.location.hash);
  useEffect(() => {
    const changed = () => setRoute(window.location.hash);
    window.addEventListener("hashchange", changed);
    return () => window.removeEventListener("hashchange", changed);
  }, []);
  return route;
}
export function Leagues() {
  const route = useLeagueRoute();
  const query = useQuery(at.tastetest.game.list, {});
  const join = useProcedure(at.tastetest.game.join, {
    invalidates: [at.tastetest.game.list],
  });
  const cancel = useProcedure(at.tastetest.game.cancel, {
    invalidates: [at.tastetest.game.list],
  });
  const [invite, setInvite] = useState("");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const match = /^#league\/([0-9a-f-]+)(\/invite)?$/.exec(route);
  const selected = match && query.data?.leagues.find((l) => l.id === match[1]);
  useEffect(() => setError(""), [route]);
  async function joinWithInvite(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const league = await join({ invite: invite.trim() });
      setInvite("");
      window.location.hash = `league/${league.id}`;
    } catch {
      setError(
        "Could not join. Check your invite code; the league may be full.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function cancelSetup(league: string) {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await cancel({ league });
      await query.refetch();
      window.location.hash = "leagues";
    } catch {
      setError(
        "Could not cancel setup. It may already have saved rounds. Refresh to check.",
      );
    } finally {
      setBusy(false);
    }
  }
  if (route === "#new-league") return <CreateLeague />;
  if (match)
    return (
      <section className="league-workspace">
        <a href="#leagues">← Your leagues</a>
        {query.isPending || (!selected && query.isFetching) ? (
          <p role="status">Loading league…</p>
        ) : selected ? (
          <>
            <h2>{match[2] ? `Invite to ${selected.name}` : selected.name}</h2>
            {match[2] ? (
              <>
                <label>
                  Invite code
                  <input
                    readOnly
                    value={selected.id}
                    onFocus={(e) => e.target.select()}
                  />
                </label>
                <p className="muted">Share this code with your players.</p>
                <a href={`#league/${selected.id}`}>Back to league</a>
              </>
            ) : (
              <>
                <div className="league-toolbar">
                  <span>
                    {selected.memberCount}/{selected.maxPlayers} players
                  </span>
                  <a href={`#league/${selected.id}/invite`}>Invite players</a>
                </div>
                {error && (
                  <p role="alert" className="error">
                    {error}
                  </p>
                )}
                <RoundWorkspace
                  league={selected.id}
                  isAdmin={selected.isAdmin}
                  testType={selected.testType}
                  onCancelSetup={() => void cancelSetup(selected.id)}
                  cancelling={busy}
                />
              </>
            )}
          </>
        ) : (
          <p role="alert">
            {query.error
              ? "Could not load this league. Refresh to retry."
              : "This league isn’t available to your account."}
          </p>
        )}
      </section>
    );
  return (
    <section className="league-workspace" aria-labelledby="leagues-title">
      <div className="league-toolbar">
        <h2 id="leagues-title">Your leagues</h2>
        <a className="button-link" href="#new-league">
          Create a league
        </a>
      </div>
      {query.isPending && <p role="status">Loading your leagues…</p>}
      {query.error && (
        <p role="alert">
          Could not load leagues.{" "}
          <button onClick={() => void query.refetch()}>Retry</button>
        </p>
      )}
      {query.data?.leagues.length === 0 && (
        <p>You haven’t joined a league yet.</p>
      )}
      <ul className="league-list">
        {query.data?.leagues.map((league) => (
          <li key={league.id}>
            <h3>
              <a href={`#league/${league.id}`}>{league.name}</a>
            </h3>
            <p className="muted">
              {league.testType === "music" ? "Music Test" : "Skeet Test"} ·{" "}
              {league.memberCount}/{league.maxPlayers} players
              {league.isAdmin ? " · Host" : ""}
            </p>
          </li>
        ))}
      </ul>
      <form onSubmit={(event) => void joinWithInvite(event)}>
        <h3>Join with an invite</h3>
        <label>
          Invite code
          <input
            value={invite}
            onChange={(e) => setInvite(e.target.value)}
            maxLength={100}
            required
            disabled={busy}
          />
        </label>
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        <button disabled={busy || !invite.trim()}>
          {busy ? "Joining…" : "Join league"}
        </button>
      </form>
    </section>
  );
}
function CreateLeague() {
  const create = useProcedure(at.tastetest.game.create, {
    invalidates: [at.tastetest.game.list, at.tastetest.play.get],
  });
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"music" | "skeet">("music");
  const [capacity, setCapacity] = useState(20);
  const [requestId] = useState(() => crypto.randomUUID());
  const plan = useRoundPlan();
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [pending, setPending] = useState<Parameters<typeof create>[0] | null>(
    null,
  );
  async function save(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const rounds = plan.plans();
    if (
      !pending &&
      (!name.trim() ||
        !rounds.length ||
        rounds.some((r) => !r.theme.trim()) ||
        rounds[0].submissionEndsAt <= Date.now())
    ) {
      setError(
        "Add a theme for each round and choose a future first submission deadline.",
      );
      return;
    }
    const input = pending ?? {
      requestId,
      name: name.trim(),
      testType: kind,
      maxPlayers: capacity,
      rounds,
    };
    setBusy(true);
    setError("");
    setPending(input);
    try {
      const league = await create(input);
      window.location.hash = `league/${league.id}/invite`;
    } catch {
      setError(
        "Could not confirm creation. Retry to check the same league, or return to your leagues.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="league-workspace" aria-labelledby="create-league-title">
      <h2 id="create-league-title">Create a league</h2>
      <form onSubmit={(event) => void save(event)}>
        <fieldset className="league-setup-fields" disabled={busy || !!pending}>
          <label>
            League name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              required
            />
          </label>
          <fieldset className="test-choices">
            <legend>Choose your test</legend>
            <div className="test-choice-grid">
              {(
                [
                  {
                    value: "music",
                    title: "Music Test",
                    description: "Submit a song that fits each round’s theme.",
                    icon: "♫",
                  },
                  {
                    value: "skeet",
                    title: "Skeet Test",
                    description: "Submit the best skeet of the week.",
                    icon: "↗",
                  },
                ] as const
              ).map((choice) => (
                <label
                  key={choice.value}
                  className={`test-choice ${kind === choice.value ? "is-selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="test-kind"
                    value={choice.value}
                    checked={kind === choice.value}
                    onChange={() => setKind(choice.value)}
                  />
                  <span className="test-choice-icon" aria-hidden="true">
                    {choice.icon}
                  </span>
                  <span>
                    <strong>{choice.title}</strong>
                    <span className="test-choice-description">
                      {choice.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <label>
            Maximum players
            <input
              type="number"
              min={4}
              max={20}
              value={capacity}
              onChange={(e) => setCapacity(Number(e.target.value))}
              required
            />
          </label>
          <RoundPlanFields
            plan={plan}
            testType={kind}
            busy={busy || !!pending}
          />
        </fieldset>
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        <div className="actions">
          <button disabled={busy}>
            {busy ? "Saving…" : pending ? "Retry creation" : "Create league"}
          </button>
          <a
            href="#leagues"
            aria-disabled={busy}
            onClick={(e) => {
              if (busy) e.preventDefault();
            }}
          >
            {pending ? "Back to leagues" : "Cancel"}
          </a>
        </div>
      </form>
    </section>
  );
}
